import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { SparkRenderer } from '@sparkjsdev/spark'
import { SPARK_TRAINING_BLUR } from '../ogs/render/sparkBlur.js'
import { HairRenderer } from '../hair/HairRenderer.js'
import { assetUrl } from '../lib/assetUrl.js'
import { hairLoadProgress } from '../lib/hairLoadProgress.js'

// Maps the hair's authored space (Myra training, cm) into the runtime head
// bone's local frame, so the splat rides `head` and its roots sit on the real
// scalp. Derived by ICP-registering the training scalp mesh to the runtime
// head_lod0_mesh (median residual 0.17 cm), then folding in the head-bone
// transform:  M = inv(headBone.worldAtSample) · scale(0.01) · T_world.
// Column-major (THREE order). Myra-specific; regenerate per character.
const MYRA_HAIR_ATTACH_M = [
  0.01759542, -0.00751146, -0.99819601, 0.0,
  0.99813551, -0.01322034, 0.01769400, 0.0,
  -0.01335083, -0.99826395, 0.00727668, 0.0,
  -131.92159262, 2.90334390, -2.45926745, 1.0,
]

// Locate the MetaHuman "head" bone on the live avatar. Mirrors the
// resolver in HeadModel.jsx so the two stay in agreement about which
// joint the head splat tracks.
function findHeadBoneIn(root) {
  if (!root) return null
  let exact = null
  let loose = null
  root.traverse((o) => {
    if (!o.isBone) return
    if (!exact && /^head(_m)?$/i.test(o.name)) exact = o
    else if (!loose && /head/i.test(o.name) && !/forehead|headtop/i.test(o.name)) loose = o
  })
  return exact || loose
}

// Loads `public/assets/hair_static/*` (PLY + 4 small ONNX nets).
// The BecomingLit shading equations sample the volume light-probe SH
// (window.__LIGHT_PROBE__, installed by LightProbeVolume) live each shade —
// the same source the ?debug_env=1 oracle ball uses.
export default function HairSplat({
  enabled,                    // initial visibility (default: URL ?hair=1 or false)
  useNeuralBsdf = true,
  useSpecMod = true,
  assetsBaseUrl = '/assets/hair_static',
  attachMatrix = null,        // per-character head-attach matrix (16 floats, column-major); falls back to Myra's
  followBone = 'head',        // bone name to track on window.__AVATAR_SCENE__; pass null to keep splat static
  worldScale = 1,             // cm→m scale for a static splat (followBone=null); lock-step with the face splat
  hideAvatarHair = true,      // hide the avatar's polygonal hair mesh(es) so only the splat hair shows
  onReady,                    // (hair) => void
  onError,                    // (err) => void
}) {
  const gl     = useThree(s => s.gl)
  const scene  = useThree(s => s.scene)
  const camera = useThree(s => s.camera)

  // Look for an existing SparkRenderer. If none, mount our own
  // Hooks the WebGLRenderer once per SparkRenderer instance, so two would
  // double-render splats. Tracked across remounts via a `gl.__sparkRenderer`
  // tag so we don't accidentally spawn duplicates within a single canvas.
  const sparkRenderer = useMemo(() => {
    if (!gl) return null
    let found = null
    scene.traverse((c) => { if (!found && c instanceof SparkRenderer) found = c })
    if (found) return null            // someone else owns it; we won't add a second
    if (gl.__hairSparkRenderer) return gl.__hairSparkRenderer
    try {
      const sr = new SparkRenderer({ renderer: gl, ...SPARK_TRAINING_BLUR })
      gl.__hairSparkRenderer = sr
      return sr
    } catch (err) {
      console.warn('[HairSplat] SparkRenderer init failed:', err)
      onError?.(err)
      return null
    }
  }, [gl, scene]) // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve initial enable flag. Visible by default; disable explicitly
  // with `?hair=0` or by passing `enabled={false}`.
  const initialEnabled = useMemo(() => {
    if (typeof enabled === 'boolean') return enabled
    if (typeof window === 'undefined') return true
    return new URLSearchParams(window.location.search).get('hair') !== '0'
  }, [enabled])

  const [hair, setHair] = useState(null)
  const initStartedRef = useRef(false)

  // Head attachment. We have scaled the mesh from cm to m
  const headBoneRef        = useRef(null)
  const attachMatRef       = useMemo(
    () => new THREE.Matrix4().fromArray(
      (Array.isArray(attachMatrix) && attachMatrix.length === 16) ? attachMatrix : MYRA_HAIR_ATTACH_M),
    [attachMatrix])
  const userDeltaRef       = useMemo(() => new THREE.Vector3(), [])   // optional world-space nudge
  const userDeltaMatRef    = useMemo(() => new THREE.Matrix4(), [])
  const deltaMatRef        = useMemo(() => new THREE.Matrix4(), [])
  const hiddenHairRef      = useRef(null)   // [{mesh, prev}] avatar hair meshes we hid
  const progressTokenRef   = useRef(null)   // hairLoadProgress token for this load

  // Build the HairRenderer once we have gl + scene + camera. 
  useEffect(() => {
    if (!gl || !scene || !camera) return
    if (initStartedRef.current) return
    initStartedRef.current = true
    // Per-invocation cancellation. A shared ref breaks under React StrictMode's
    // mount→unmount→mount: the 2nd mount reset the shared flag to false before
    // the 1st mount's async create() resolved, so neither got cancelled and BOTH
    // HairRenderers survived (the strand splat rendered + shaded twice). A local
    // captured by this effect's closure is private to this mount, so the orphaned
    // (unmounted) instance disposes itself on resolve.
    let cancelled = false

    let instance = null
    const progressToken = hairLoadProgress.start()
    progressTokenRef.current = progressToken
    ;(async () => {
      try {
        instance = await HairRenderer.create({
          renderer: gl,
          scene,
          camera,
          assetsBaseUrl,
          flags: {
            enabled: initialEnabled,
            useNeuralBsdf,
            useSpecMod,
          },
          onStep: (name) => { console.debug('[hair] step:', name); hairLoadProgress.step(name) },
        })
        if (cancelled) {
          try { instance.dispose() } catch { /* ignore */ }
          hairLoadProgress.fail(progressToken)
          return
        }
        setHair(instance)
        if (typeof window !== 'undefined') {
          window.hair = instance
          window.__hair_show__ = (v) => {
            instance.setFlag('enabled', v !== false)
            return instance.isVisible()
          }
        }
        console.log('[HairSplat] ready (env=volume, enabled=%s)',
          instance.isVisible())
        // Static splat (no head tracking) is visible now → finish. Head-tracked
        // splats finish at the actual poly→splat swap in useFrame below, so the
        // overlay holds until the hair really changes on screen.
        if (!followBone) hairLoadProgress.finish(progressToken)
        onReady?.(instance)
      } catch (e) {
        console.warn('[HairSplat] init failed:', e)
        hairLoadProgress.fail(progressToken)
        onError?.(e)
      }
    })()

    return () => {
      cancelled = true
      // Unmounting (character switch, None selected) — clear the load overlay
      // immediately rather than waiting for the in-flight create() to resolve
      // ~40s later. Otherwise switching away from Myra mid-load leaves her hair
      // progress bar showing over the new character.
      hairLoadProgress.fail(progressTokenRef.current)
      if (instance) {
        try { instance.dispose() } catch { /* ignore */ }
      }
      if (typeof window !== 'undefined') {
        if (window.hair === instance) delete window.hair
        if (window.__hair_show__) delete window.__hair_show__
        if (window.__hair_nudge__) delete window.__hair_nudge__
        if (window.__hair_align__) delete window.__hair_align__
        if (window.__hair_set_delta__) delete window.__hair_set_delta__
        if (window.__hair_get_delta__) delete window.__hair_get_delta__
      }
      initStartedRef.current = false
      headBoneRef.current = null
      if (hiddenHairRef.current) {
        for (const { mesh, prev } of hiddenHairRef.current) mesh.visible = prev
        hiddenHairRef.current = null
      }
      setHair(null)
    }
  }, [gl, scene, camera]) // eslint-disable-line react-hooks/exhaustive-deps

  // Static splat (followBone=null, e.g. isaac-fused): scale cm→m once the
  // renderer is up. camPosInSplatLocal inverts splatMesh.matrix, so hair shading
  // stays in the training cm frame and the splat fuses with the (equally scaled)
  // face splat. The head-tracked path applies scale via attachMatrix instead.
  useEffect(() => {
    if (!hair?.splatMesh || followBone || worldScale === 1) return
    const mesh = hair.splatMesh
    mesh.matrixAutoUpdate = false
    mesh.matrix.makeScale(worldScale, worldScale, worldScale)
    mesh.matrixWorldNeedsUpdate = true
  }, [hair, followBone, worldScale])

  // Per-frame update — view_enc + spec_mod + shade on camera settle, plus
  // head-bone tracking when followBone is set.
  // Guards against missing instance / disable flag are inside hair.update().
  useFrame((_, delta) => {
    if (!hair) return
    try { hair.update(delta) } catch (e) { console.warn('[hair] update threw:', e) }

    if (!followBone || !hair.splatMesh) return

    // First-time attach: locate the head bone (the avatar may mount after the
    // splat) and hide the polygonal hair. 
    if (!headBoneRef.current) {
      const avatarRoot = typeof window !== 'undefined' ? window.__AVATAR_SCENE__ : null
      if (!avatarRoot) return
      const bone = avatarRoot.getObjectByName(followBone) || findHeadBoneIn(avatarRoot)
      if (!bone) return
      headBoneRef.current = bone
      hair.splatMesh.matrixAutoUpdate = false

      // Hide the avatar's polygonal hair mesh(es) so only the splat hair shows.
      if (hideAvatarHair && !hiddenHairRef.current) {
        const hidden = []
        avatarRoot.traverse((o) => {
          if (o.isMesh && /hair/i.test(o.name) && o.visible) {
            hidden.push({ mesh: o, prev: o.visible })
            o.visible = false
          }
        })
        hiddenHairRef.current = hidden
        console.info(`[HairSplat] hid ${hidden.length} avatar hair mesh(es): ` +
          hidden.map((h) => h.mesh.name).join(', '))
      }
      // The poly→splat swap just happened on screen — NOW the load is visually
      // complete. Clear the progress overlay here (not at "ready"), so the
      // indicator and the actual hair change stay in sync.
      hairLoadProgress.finish(progressTokenRef.current)
    }

    const bone = headBoneRef.current
    bone.updateMatrixWorld()
    const mesh = hair.splatMesh
    // splat world = userDelta · headBone.world · attachMat
    deltaMatRef.multiplyMatrices(bone.matrixWorld, attachMatRef)
    if (userDeltaRef.lengthSq() > 0) deltaMatRef.premultiply(userDeltaMatRef)
    mesh.matrix.copy(deltaMatRef)
    mesh.matrixWorldNeedsUpdate = true

  })

  // Spark needs a SparkRenderer in the scene graph; mount our own if no one
  // else has. HairRenderer.scene.add(splatMesh) handles the SplatMesh itself.
  return sparkRenderer ? <primitive object={sparkRenderer} /> : null
}
